import React, { useEffect, useRef, useState } from 'react';
import { TileLayer, MapContainer, WMSTileLayer, FeatureGroup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import 'leaflet-side-by-side';
import { EditControl } from 'react-leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import { getCenterAndZoom } from './custom/MapFunctions';
import {
  postGetPlantNetSpeciesPrediction,
  postGetPlantnetPlotsSpeciesObservation,
  postGetPlantnetOccurrencesSpeciesObservation,
} from '../../externalAPI/planetAPI';
import { getPostMinkaSpeciesObservation } from '../../externalAPI/minkaAPI';
import ExportControl from './custom/MapExportLayers';
import GeoJsonControl from './custom/MapFranceCaseStudyLayers';
import SpainControl from './custom/MapSpainLayers';
import LeafletControlGeocoder from './custom/MapSearch';
import { HistoricalMapControl } from './custom/MapFranceHisotricalLayers';
import { message } from 'antd';
import { customCaseStudiesSpecies } from '../case/caseSpecies';
import BaseMapLayerControl from './custom/MapChangeMapLayer';
import { getGBIFSpeciesObservation } from '../../externalAPI/gbifAPI';
import Cookies from 'js-cookie';
import { RailwayMapControl } from './custom/MapFranceRailwayLayers';
import { SpeciesV2 } from '../../interfaces';
import { getSinpData } from '../elements/SpeciesSinp';

////////////////////////
//  Observations Map  //
////////////////////////

interface ObservationMapProps {
  api: 'minka' | 'plantnet_plots' | 'plantnet_occurrences' | 'gbif' | 'sinp';
  language: string;
  onDataRetrieved: (data: any[]) => void;
}

const ObservationMap: React.FC<ObservationMapProps> = ({ api, language, onDataRetrieved }) => {
  const caseStudy = Cookies.get('caseStudy');
  const [messageApi, contextHolder] = message.useMessage();
  const featureGroupRef = useRef<L.FeatureGroup>(null);
  const [areaSelected, setAreaSelected] = useState<any>(null);
  const [exportControlRef, setExportControlRef] = useState<any>(null);
  const [customGeoJsonCaseStudyControlRef, setCustomGeoJsonCaseStudyControlRef] = useState<any>(null);
  const [customSpainControlRef, setCustomSpainControlRef] = useState<any>(null);
  const [franceHistoricalControlRef, setFranceHistoricalControlRef] = useState<any>(null);
  const [exportRailwayLayerRef, setExportRailwayLayerRef] = useState<any>(null);
  const [sinpSpeciesData, setSinpSpeciesData] = useState<SpeciesV2[]>([]);

  const MapInitializer: React.FC = () => {
    const map = useMap();

    useEffect(() => {
      if (caseStudy && !areaSelected) {
        const { center, zoom } = getCenterAndZoom(caseStudy);
        map.setView(center, zoom);
      }
      if (!exportControlRef) {
        const exportControl = new ExportControl({ position: 'topright' });
        exportControl.addTo(map);
        setExportControlRef(exportControl);
      }
      if (!customGeoJsonCaseStudyControlRef) {
        const geoJsonControl = new GeoJsonControl({ position: 'bottomleft' });
        geoJsonControl.addTo(map);
        setCustomGeoJsonCaseStudyControlRef(geoJsonControl);
      }
      if (!exportRailwayLayerRef && caseStudy === 'pnr_narbonnaise') {
        const exportRailwayLayer = new RailwayMapControl({ position: 'bottomleft' });
        exportRailwayLayer.addTo(map);
        setExportRailwayLayerRef(exportRailwayLayer);
      }
      if (!customSpainControlRef && caseStudy === 'barcelona') {
        const geoJsonControl = new SpainControl({
          position: 'bottomleft',
          onAreaSelected: (e) => {
            // This will set the areaSelected state with the clicked feature
            handleCreated(e);
          },
        });
        geoJsonControl.addTo(map);
        setCustomSpainControlRef(geoJsonControl);
      }
      if (!franceHistoricalControlRef && caseStudy === 'pnr_narbonnaise') {
        const franceHistoricalControl = new HistoricalMapControl({ position: 'bottomleft' });
        franceHistoricalControl.addTo(map);
        setFranceHistoricalControlRef(franceHistoricalControl);
      }
    }, []);
    return null; // No UI rendering
  };
  useEffect(() => {
    if (api === 'sinp' && sinpSpeciesData.length === 0) {
      addSinpMapLayers();
    }

    if (areaSelected === null) {
      onDataRetrieved([]); // Clear data
      return;
    }
    if (api === 'plantnet_plots') {
      updatePlantnetPlotsObservations(areaSelected.layer.toGeoJSON().geometry, language);
    } else if (api === 'plantnet_occurrences') {
      updatePlantnetOccurrencesObservations(areaSelected.layer.toGeoJSON().geometry, language);
    } else if (api === 'minka') {
      updateMinkaObservations(areaSelected.layer.getBounds(), language);
    } else if (api === 'gbif') {
      updateGBIFObservations(areaSelected.layer.getBounds(), language);
    } else if (api === 'sinp') {
      onDataRetrieved(filterSpeciesInBounds(sinpSpeciesData, areaSelected.layer.getBounds())); // Pass filtered data back to parent
    }
  }, [api, language, areaSelected, caseStudy, sinpSpeciesData]);

  const filterSpeciesInBounds = (data: SpeciesV2[], bounds: L.LatLngBounds) => {
    if (!bounds || !data || data.length === 0) {
      return [];
    }
    const filtered = data.filter((specie) => {
      // Ensure latitude and longitude exist for the species
      if (typeof specie.latitude === 'number' && typeof specie.longitude === 'number') {
        // Check if the species' coordinates are within the bounds
        return bounds.contains(L.latLng(specie.latitude, specie.longitude));
      }
      return false; // Exclude species without valid coordinates
    });
    return filtered;
  };

  const updateMinkaObservations = async (bounds: any, language: string) => {
    try {
      const [speciesList, error] = await getPostMinkaSpeciesObservation(bounds, language);
      if (error) {
        messageApi.warning(error);
      } else if (speciesList.length === 0) {
        messageApi.warning('No data on the selected area');
      } else {
        messageApi.success('Data found on the area');
      }
      const filteredSpeciesList = await customCaseStudiesSpecies(speciesList, 'Observations');
      onDataRetrieved(filteredSpeciesList); // Pass data back to parent
    } catch (error) {
      console.error('Failed to fetch Minka observations:', error);
    }
  };
  const updatePlantnetPlotsObservations = async (area: any, language: string) => {
    try {
      area = updateGeoCoordinatesToClockwiseForPlantnetApi(area);
      const [speciesList, error] = await postGetPlantnetPlotsSpeciesObservation(area, language);
      if (error) {
        messageApi.warning(error);
      } else if (speciesList.length === 0) {
        messageApi.warning('No data on the selected area');
      } else {
        messageApi.success('Data found on the area');
      }
      const filteredSpeciesList = await customCaseStudiesSpecies(speciesList, 'Observations');
      onDataRetrieved(filteredSpeciesList);
    } catch (error) {
      console.error('Failed to fetch Plantnet observations:', error);
    }
  };

  const updatePlantnetOccurrencesObservations = async (area: any, language: string) => {
    try {
      area = updateGeoCoordinatesToClockwiseForPlantnetApi(area);
      const [speciesList, error] = await postGetPlantnetOccurrencesSpeciesObservation(area, language);
      if (error) {
        messageApi.warning(error);
      } else if (speciesList.length === 0) {
        messageApi.warning('No data on the selected area');
      } else {
        messageApi.success('Data found on the area');
      }
      const filteredSpeciesList = await customCaseStudiesSpecies(speciesList, 'Observations');
      onDataRetrieved(filteredSpeciesList);
    } catch (error) {
      console.error('Failed to fetch Plantnet observations:', error);
    }
  };
  const updateGBIFObservations = async (area: any, language: string) => {
    try {
      const [speciesList, error] = await getGBIFSpeciesObservation(area, language);
      if (error) {
        messageApi.warning(error);
      } else if (speciesList.length === 0) {
        messageApi.warning('No data on the selected area');
      } else {
        messageApi.success('Data found on the area');
      }
      const filteredSpeciesList = await customCaseStudiesSpecies(speciesList, 'Observations');
      onDataRetrieved(filteredSpeciesList);
    } catch (error) {
      console.error('Failed to fetch Plantnet observations:', error);
    }
  };
  const handleCreated = async (e: any) => {
    // Update Selected Area
    setAreaSelected(e);
    // Clear other layers in the feature group (optional)
    if (featureGroupRef.current) {
      const layers = featureGroupRef.current.getLayers();
      layers.forEach((layer) => {
        if (layer !== e.layer) {
          featureGroupRef.current?.removeLayer(layer);
        }
      });
    }
  };

  const addSinpMapLayers = async () => {
    const MAX_RETRIES = 5;
    let attempts = 0;
    let success = false;

    // Keep looping until success or max attempts are reached
    while (attempts < MAX_RETRIES && !success) {
      attempts++;

      // Show loading message only on the first attempt
      if (attempts === 1) {
        messageApi.loading('Loading SINP data...');
      } else {
        // Provide feedback on retries
        messageApi.warning(`SINP data load failed. Retrying... (Attempt ${attempts} of ${MAX_RETRIES})`);
      }

      try {
        const data = await getSinpData(); // Attempt to fetch data

        if (data && data.length > 0) {
          // SUCCESS condition
          setSinpSpeciesData(data); // Set the fetched data to state
          messageApi.success('SINP data loaded successfully!');
          success = true; // Exit the loop
        } else {
          // Treat no data found as a soft failure (warning, but don't retry)
          setSinpSpeciesData([]);
          messageApi.warning('No SINP data found.');
          success = true; // Exit the loop on warning/no data found
        }
      } catch (error) {
        // ERROR condition
        console.error(`Attempt ${attempts} failed to fetch SINP data:`, error);

        // If this was the final attempt, log a hard error
        if (attempts === MAX_RETRIES) {
          messageApi.error('Failed to load SINP data after multiple attempts.');
          setSinpSpeciesData([]);
        }

        // Introduce a short delay before retrying (optional, but recommended)
        if (attempts < MAX_RETRIES) {
          await new Promise(resolve => setTimeout(resolve, 1500)); // Wait 1.5 seconds
        }
      }
    }
  };

  return (
    <div>
      {contextHolder}
      <MapContainer
        id='observationMapId'
        center={[0, 0]}
        zoom={0}
        style={{ height: '800px', width: '100%', borderRadius: '10px' }}
      >
        <LeafletControlGeocoder />
        <MapInitializer />
        <BaseMapLayerControl />
        {caseStudy && (
          <WMSTileLayer
            key={`${caseStudy}`}
            url={`https://maps.plantnet.org/guarden/${caseStudy}/indicators`}
            layers={'extent'}
            format='image/png'
            transparent={true}
            version='1.3.0'
            opacity={0.5}
            attribution='Your attribution text'
            zIndex={999}
          />
        )}
        {api === 'minka' && <TileLayer url='https://api.minka-sdg.org/v1/points/{z}/{x}/{y}.png' zIndex={10} />}
        <FeatureGroup ref={featureGroupRef}>
          <EditControl
            position='topright'
            onCreated={handleCreated}
            draw={{
              rectangle: true,
              polyline: false,
              circle: false,
              marker: false,
              circlemarker: false,
            }}
          />
          {/* {api === 'sinp' && sinpSpeciesData.length > 0 && <SinpMarkersLayer speciesData={sinpSpeciesData} />} */}
        </FeatureGroup>
      </MapContainer>
    </div>
  );
};

////////////////////////
//  Predictions Map  //
////////////////////////
interface PredictionsMapProps {
  language: string;
  onDataRetrieved: (data: any[]) => void;
}

const PredictionsMap: React.FC<PredictionsMapProps> = ({ language, onDataRetrieved }) => {
  const caseStudy = Cookies.get('caseStudy') || null;
  const [messageApi, contextHolder] = message.useMessage();
  const featureGroupRef = useRef<L.FeatureGroup>(null);
  const [areaSelected, setAreaSelected] = useState<any>(null);
  const [exportControlRef, setExportControlRef] = useState<any>(null);
  const [customGeoJsonCaseStudyControlRef, setCustomGeoJsonCaseStudyControlRef] = useState<any>(null);
  const [customSpainControlRef, setCustomSpainControlRef] = useState<any>(null);
  const [franceHistoricalControlRef, setFranceHistoricalControlRef] = useState<any>(null);
  const [exportRailwayLayerRef, setExportRailwayLayerRef] = useState<any>(null);

  const MapInitializer: React.FC = () => {
    const map = useMap();
    useEffect(() => {
      if (caseStudy && !areaSelected) {
        const { center, zoom } = getCenterAndZoom(caseStudy);
        map.setView(center, zoom);
      }
      if (!exportControlRef) {
        const exportControl = new ExportControl({ position: 'topright' });
        exportControl.addTo(map);
        setExportControlRef(exportControl);
      }
      if (!customGeoJsonCaseStudyControlRef) {
        const geoJsonControl = new GeoJsonControl({ position: 'bottomleft' });
        geoJsonControl.addTo(map);
        setCustomGeoJsonCaseStudyControlRef(geoJsonControl);
      }
      if (!exportRailwayLayerRef && caseStudy === 'pnr_narbonnaise') {
        const exportRailwayLayer = new RailwayMapControl({ position: 'bottomleft' });
        exportRailwayLayer.addTo(map);
        setExportRailwayLayerRef(exportRailwayLayer);
      }
      if (!customSpainControlRef && caseStudy === 'barcelona') {
        const geoJsonControl = new SpainControl({
          position: 'bottomleft',
          onAreaSelected: (e) => {
            // This will set the areaSelected state with the clicked feature
            handleCreated(e);
          },
        });
        geoJsonControl.addTo(map);
        setCustomSpainControlRef(geoJsonControl);
      }
      if (!franceHistoricalControlRef && caseStudy === 'pnr_narbonnaise') {
        const franceHistoricalControl = new HistoricalMapControl({ position: 'bottomleft' });
        franceHistoricalControl.addTo(map);
        setFranceHistoricalControlRef(franceHistoricalControl);
      }
    }, []);

    return null; // No UI rendering
  };
  useEffect(() => {
    if (areaSelected === null) {
      Cookies.set('PlantnetSpeciesPrediction', '');
      onDataRetrieved([]); // Clear data
      return;
    }
    updatePlantnetPrediction(areaSelected.layer.toGeoJSON().geometry, language);
  }, [language, areaSelected, caseStudy]);

  const updatePlantnetPrediction = async (area: any, language: string) => {
    try {
      if (caseStudy === null) {
        return;
      }
      if (!area || typeof area !== 'object' || area.type !== 'Polygon' || !area.coordinates) {
        console.error('Invalid extent format. Expected a GeoJSON Polygon.');
        return []; // Or throw an error, depending on your error handling strategy
      }
      const polygonExtent: GeoJSONPolygon = area as GeoJSONPolygon;
      const ensuredExtent = ensureUnticlockwiseWinding(polygonExtent);
      const [speciesList, error] = await postGetPlantNetSpeciesPrediction(ensuredExtent, false, caseStudy, language);
      const speciesListArray = Array.isArray(speciesList) ? speciesList : [speciesList];
      if (error) {
        messageApi.warning(error);
      } else if (speciesListArray.length === 0) {
        messageApi.warning('No data on the selected area');
      } else {
        messageApi.success('Data found on the area');
      }
      Cookies.set('PlantnetSpeciesObservation', JSON.stringify(speciesListArray));
      const filteredSpeciesList = await customCaseStudiesSpecies(speciesList, 'Predictions');
      onDataRetrieved(filteredSpeciesList);
    } catch (error) {
      console.error('Failed to fetch Plantnet observations:', error);
    }
  };

  const handleCreated = async (e: any) => {
    // Update Selected Area
    setAreaSelected(e);
    // Clear other layers in the feature group (optional)
    if (featureGroupRef.current) {
      const layers = featureGroupRef.current.getLayers();
      layers.forEach((layer) => {
        if (layer !== e.layer) {
          featureGroupRef.current?.removeLayer(layer);
        }
      });
    }
  };
  return (
    <div>
      {contextHolder}
      <MapContainer
        id='predictionsMapId'
        center={[0, 0]}
        zoom={0}
        style={{ height: '800px', width: '100%', borderRadius: '10px' }}
      >
        <LeafletControlGeocoder />
        <MapInitializer />
        <BaseMapLayerControl />
        {caseStudy && (
          <WMSTileLayer
            key={`${caseStudy}`}
            url={`https://maps.plantnet.org/guarden/${caseStudy}/indicators`}
            layers={'extent'}
            format='image/png'
            transparent={true}
            version='1.3.0'
            opacity={0.5}
            attribution='Your attribution text'
            zIndex={999}
          />
        )}
        <FeatureGroup ref={featureGroupRef}>
          <EditControl
            position='topright'
            onCreated={handleCreated}
            draw={{
              rectangle: true,
              polyline: false,
              circle: false,
              marker: false,
              circlemarker: false,
            }}
          />
        </FeatureGroup>
      </MapContainer>
    </div>
  );
};

// API fix coordenates
/**
 * Convert the input GeoJSON Polygon to a bounding box polygon declared in counter-clockwise order.
 * The order will be: [west, south], [east, south], [east, north], [west, north], [west, south]
 */
const updateGeoCoordinatesToClockwiseForPlantnetApi = (geometry: any): any => {
  if (
    !geometry ||
    geometry.type !== 'Polygon' ||
    !Array.isArray(geometry.coordinates) ||
    geometry.coordinates.length === 0
  ) {
    throw new Error('Invalid geometry format. Expected a GeoJSON Polygon.');
  }

  // Using the first ring of coordinates.
  const coords: [number, number][] = geometry.coordinates[0];

  // Reduce to get bounding box: west, south, east, north.
  const [west, south, east, north] = coords.reduce(
    ([minLng, minLat, maxLng, maxLat], [lng, lat]) => [
      Math.min(minLng, lng),
      Math.min(minLat, lat),
      Math.max(maxLng, lng),
      Math.max(maxLat, lat),
    ],
    [Infinity, Infinity, -Infinity, -Infinity],
  );

  // Update the first ring with clockwise ordered coordinates.
  geometry.coordinates[0] = [
    [west, south],
    [east, south],
    [east, north],
    [west, north],
    [west, south],
  ];

  return geometry;
};
// Prediction
interface GeoJSONPolygon {
  type: 'Polygon';
  coordinates: number[][][];
}

// Helper function to calculate the signed area of a linear ring
function calculateSignedArea(linearRing: number[][]): number {
  let area = 0;
  const numPoints = linearRing.length;
  for (let i = 0; i < numPoints; i++) {
    const p1 = linearRing[i];
    const p2 = linearRing[(i + 1) % numPoints]; // Wrap around to the first point
    area += p1[0] * p2[1] - p2[0] * p1[1];
  }
  return area / 2.0;
}

// Function to reverse the winding order of a polygon (from previous answer)
function reversePolygonWinding(polygon: GeoJSONPolygon): GeoJSONPolygon {
  const reversedPolygon: GeoJSONPolygon = {
    type: 'Polygon',
    coordinates: polygon.coordinates.map((linearRing) => [...linearRing]),
  };
  if (reversedPolygon.coordinates && reversedPolygon.coordinates[0]) {
    reversedPolygon.coordinates[0].reverse();
  }
  return reversedPolygon;
}

function ensureUnticlockwiseWinding(polygon: GeoJSONPolygon): GeoJSONPolygon {
  // Calculate the signed area of the first linear ring (assuming it's the outer boundary)
  const signedArea = calculateSignedArea(polygon.coordinates[0]);

  // If the signed area is negative, the winding is clockwise, so reverse it
  if (signedArea < 0) {
    return reversePolygonWinding(polygon);
  } else {
    // Otherwise (positive or zero area), it's already unticlockwise or degenerate, return as is (copy for immutability)
    return {
      type: 'Polygon',
      coordinates: polygon.coordinates.map((linearRing) => [...linearRing]),
    };
  }
}
export { ObservationMap, PredictionsMap };
